import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Navigate } from 'react-router';

import { connect } from 'react-redux';
import { useAppDispatch as useDispatch } from 'dux/app/hooks';

import ErrorContainer from 'Containers/ErrorContainer';

import LoadingScene from 'Scenes/LoadingScene';
import Cart from '../Scenes/Cart';

import * as checkoutCartActions from 'dux/checkoutCart/actions';
import * as checkoutCartSelectors from 'dux/checkoutCart/selectors';
import * as consultationSelectors from 'dux/consultation/selectors';
import { fetchHairProfile } from 'dux/consultation/thunks';

import { useCheckoutCategory } from '../hooks/useCheckoutCategory';

const CartContainerLoader = ({ answers, error, loadCart, params, status }) => {
  const dispatch = useDispatch();
  const checkoutCategory = useCheckoutCategory();

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    /*
    Fetch hair-profile/answers if the users didn't have them
    already as we need to send them for tracking purposes.
    We fetch this here as we want to have this info and send
    for tracking everytime user arrives or tries to arrive
    to the cart page.
    */
    if (!answers) {
      dispatch(fetchHairProfile());
    }
  }, []);

  const fetchCartData = async () => {
    await loadCart(params, checkoutCategory);
    await dispatch(checkoutCartActions.loadCartSectionsTitle());
    setIsReady(true);
  };

  useEffect(() => {
    // this is executed after a first render, so there is a first render with the previous `status` (which could be "success")
    fetchCartData();
  }, []);

  // `isReady` is to handle the edge-case where we come back to the Cart and the `status` is still "success" from before,
  // triggering an unwanted render and the children's useEffects (e.g. displaying the previous Toast while Cart is re-fetching).
  if (!isReady) {
    return <LoadingScene />;
  }

  switch (status) {
    case 'loading':
    case 'idle':
      return <LoadingScene />;
    case 'success':
    case 'saving':
      return <Cart checkoutCategory={checkoutCategory} />;
    case 'finished':
      // user should fall here if returning from checkout success
      return <Navigate replace to="/account/history" />;
    case 'error':
    default:
      return (
        <ErrorContainer
          error={error}
          retry={() => loadCart(params, checkoutCategory)}
          sentryMessage="Invalid state on CartContainerLoader"
        />
      );
  }
};

CartContainerLoader.propTypes = {
  answers: PropTypes.shape({
    survey_hair_texture: PropTypes.string,
  }),
  error: PropTypes.shape({}),
  loadCart: PropTypes.func.isRequired,
  params: PropTypes.shape({}).isRequired,
  status: PropTypes.string.isRequired,
};

CartContainerLoader.defaultProps = {
  answers: null,
  error: null,
};

export default connect(
  state => ({
    error: checkoutCartSelectors.getCartError(state),
    status: checkoutCartSelectors.getCartStatus(state),
    answers: consultationSelectors.getConsultationAnswers(state),
  }),
  {
    loadCart: checkoutCartActions.loadCart,
  }
)(CartContainerLoader);
